import { useTheme } from 'next-themes'
import { type ReactElement, useEffect, useState } from 'react'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism'
import type { TraceFileResponse } from '../types'

export interface SourceViewerProps {
  /** Returns a TraceFileResponse promise, or null if not found.
   *  Page wires this to KgTraceApi.getFile(correlationId, path). */
  fetchFile: (path: string) => Promise<TraceFileResponse | null>
  /** Currently-selected file path. When null, viewer is idle. */
  path: string | null
}

/**
 * Read-only source viewer for a KG file at the trace's pinned
 * git_sha. Uses react-syntax-highlighter (Prism) instead of Monaco
 * — both meet the read-only forensics requirement; Prism is already
 * a dep + ~10x lighter on bundle size. Monaco can be swapped in
 * later if engineers ask for find/replace / folding / TS-aware
 * features.
 *
 * When the trace's pinned SHA worktree has been GC'd, the backend
 * falls back to `current` and sets `served_from_current`; we render
 * a banner so the user knows what they see may not match what the
 * agent saw.
 */
export function SourceViewer({ fetchFile, path }: SourceViewerProps): ReactElement {
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === 'dark'
  const [file, setFile] = useState<TraceFileResponse | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!path) {
      setFile(null)
      setError(null)
      return
    }
    let cancelled = false
    // Clear stale file content when switching paths so the previous
    // file's syntax-highlighted body never flashes for the new path
    // before the fetch resolves.
    setFile(null)
    setLoading(true)
    setError(null)
    fetchFile(path)
      .then((result) => {
        if (cancelled) return
        if (!result) setError(`File not found: ${path}`)
        else setFile(result)
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load file.')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [path, fetchFile])

  if (!path) {
    return (
      <div className="text-sm text-muted-foreground italic p-4" data-testid="source-viewer-idle">
        Click a file path to inspect its source.
      </div>
    )
  }

  if (loading) {
    return (
      <div className="text-sm text-muted-foreground p-4" data-testid="source-viewer-loading">
        Loading {path}…
      </div>
    )
  }

  if (error || !file) {
    return (
      <div className="p-4" data-testid="source-viewer-error">
        <p className="text-sm text-red-700 dark:text-red-400">{error ?? 'File unavailable.'}</p>
        <p className="text-xs text-muted-foreground mt-2 font-mono">{path}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full" data-testid="source-viewer">
      <header className="px-3 py-2 border-b text-xs font-mono flex items-center justify-between bg-muted">
        <span>{file.path}</span>
        <span className="text-muted-foreground">
          {file.git_sha ? file.git_sha.substring(0, 12) : 'no-sha'}
        </span>
      </header>
      {file.served_from_current && (
        <div
          className="px-3 py-1.5 text-xs bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-200 border-b"
          data-testid="source-viewer-stale-banner"
        >
          ⚠️ Pinned-SHA worktree was GC&apos;d; showing latest{' '}
          <code className="font-mono">current</code>. May not match what the agent saw.
        </div>
      )}
      <div className="flex-1 overflow-auto">
        <SyntaxHighlighter
          language={detectLanguage(file.path)}
          style={isDark ? oneDark : oneLight}
          showLineNumbers
          wrapLongLines={false}
          customStyle={{
            margin: 0,
            padding: '0.75rem',
            fontSize: '0.75rem',
            background: isDark ? 'var(--dark-muted)' : 'white',
          }}
          codeTagProps={{ style: { fontFamily: 'ui-monospace, monospace' } }}
        >
          {file.content}
        </SyntaxHighlighter>
      </div>
    </div>
  )
}

/**
 * Pick a Prism language from the file extension. Defaults to
 * `typescript` since most KG files are auto-generated TS.
 */
export function detectLanguage(path: string): string {
  if (path.endsWith('.ts') || path.endsWith('.tsx')) return 'typescript'
  if (path.endsWith('.js') || path.endsWith('.jsx')) return 'javascript'
  if (path.endsWith('.json')) return 'json'
  if (path.endsWith('.yaml') || path.endsWith('.yml')) return 'yaml'
  if (path.endsWith('.md')) return 'markdown'
  return 'typescript'
}
